import type React from "react";
import {useEffect} from "react";
import {Group, Panel, useGroupRef, type GroupImperativeHandle, type PanelImperativeHandle} from "react-resizable-panels";
import {COLLAPSED_PERCENT_MAX, useCollapsiblePanel} from "~/components/resizable-split/useCollapsiblePanel";
import {RESIZABLE_SPLIT_CLASS, SplitSeparator} from "~/components/resizable-split/SplitSeparator";
import {embeddedDashboardKey} from "~/pages/chat-agent/utils/embeddedDashboardKey";
import {useChatContext} from "./context/ChatContext";
import {ChatArea} from "./components/ChatArea/ChatArea";
import {SupersetDashboardPanel} from "./components/SupersetDashboardPanel/SupersetDashboardPanel";

const SPLIT_LAYOUT = {"chat-panel": 45, "dashboard-panel": 55} as const;

function isChatPanelCollapsed(group: GroupImperativeHandle | null, chatPanel: PanelImperativeHandle | null) {
    if (chatPanel?.isCollapsed()) return true;
    const chatPercent = group?.getLayout()["chat-panel"];
    return chatPercent !== undefined && chatPercent < COLLAPSED_PERCENT_MAX;
}

export const ChatPage: React.FC = () => {
    const {clearSessionLoading, embeddedDashboard} = useChatContext();
    const groupRef = useGroupRef();
    const {
        panelRef: chatPanelRef, collapsed: isChatCollapsed, setCollapsed: setIsChatCollapsed, onResize,
    } = useCollapsiblePanel();

    // Chat cannot stay collapsed after the dashboard is gone.
    if (!embeddedDashboard && isChatCollapsed) {
        setIsChatCollapsed(false);
    }

    // ChatProvider outlives this page — clear restore/switch loading on unmount.
    useEffect(() => {
        return () => {
            clearSessionLoading();
        };
    }, [clearSessionLoading]);

    // Expanding restores the 45/55 split rather than the chat's last width.
    const onToggleFullscreen = () => {
        const group = groupRef.current;
        const chatPanel = chatPanelRef.current;
        if (!group || !chatPanel || Object.keys(group.getLayout()).length !== 2) return;

        if (isChatPanelCollapsed(group, chatPanel)) {
            group.setLayout(SPLIT_LAYOUT);
        } else {
            chatPanel.collapse();
        }
    };

    return (
        <Group
            id="chat-dashboard-split"
            orientation="horizontal"
            className={`chat-split-group ${RESIZABLE_SPLIT_CLASS}`}
            groupRef={groupRef}
        >
            {/* Always mounted so input draft and scroll survive dashboard fullscreen. */}
            <Panel
                id="chat-panel"
                panelRef={chatPanelRef}
                collapsible={Boolean(embeddedDashboard)}
                collapsedSize={0}
                minSize={embeddedDashboard ? 530 : undefined}
                defaultSize={embeddedDashboard ? "45%" : undefined}
                className="chat-split-panel"
                onResize={onResize}
            >
                <ChatArea/>
            </Panel>
            {embeddedDashboard && <SplitSeparator id="chat-dashboard-separator"/>}
            {embeddedDashboard && (
                <Panel
                    id="dashboard-panel"
                    minSize={420}
                    defaultSize="55%"
                    className="chat-split-panel"
                >
                    <SupersetDashboardPanel
                        key={embeddedDashboardKey(embeddedDashboard)}
                        dashboard={embeddedDashboard}
                        isFullscreen={isChatCollapsed}
                        onToggleFullscreen={onToggleFullscreen}
                    />
                </Panel>
            )}
        </Group>
    );
};
